/**
 * CustomerRegistrationPage — in-portal "upgrade your account" form.
 *
 * Some citizen tokens (e.g. `WALK_IN` grade or paste-a-JWT dev sessions)
 * may land on the portal without a fully-registered Customer record. This
 * page calls the same `POST /v1/customers` endpoint the public auth-flow
 * register page uses. The two pages share `registerCustomer()`; the
 * difference is purely framing — this one runs inside the authenticated
 * portal shell.
 *
 * RBAC: `Customer:create` + `@OperatorScoped` — the same Phase 1a deferral
 * applies. On 401/403 the form surfaces the inline notice already
 * established in RegisterPage.
 */
import { useState, type FormEvent, type JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2, ShieldCheck, User } from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import {
  registerCustomer,
  type CustomerRegistrationRequest,
  type RegistrationResult,
} from '../../lib/api/customer';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { useCustomerProfile } from './useCustomerProfile';
import { PageHeader } from './PortalUI';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green';

interface FormState {
  readonly fullName: string;
  readonly displayName: string;
  readonly primaryPhone: string;
  readonly primaryEmail: string;
}

const INITIAL: FormState = {
  fullName: '',
  displayName: '',
  primaryPhone: '',
  primaryEmail: '',
};

export function CustomerRegistrationPage(): JSX.Element {
  const { token } = useCustomerAuth();
  const profile = useCustomerProfile();
  const [form, setForm] = useState<FormState>(INITIAL);

  const mutation = useMutation<RegistrationResult, Error, CustomerRegistrationRequest>({
    mutationFn: (body) => registerCustomer(body, { fetchImpl: undefined as unknown as typeof fetch }),
  });

  const alreadyRegistered = profile.data?.grade === 'REGISTERED';

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (form.fullName.trim().length < 2 || form.primaryPhone.trim().length < 7) return;
    // Note: this in-portal page predates the public-registration flow; the
    // public RegisterPage (apps/web-customer/src/features/auth/RegisterPage.tsx)
    // is the canonical citizen sign-up path. This in-portal flow is retained
    // for the rare WALK_IN → REGISTERED upgrade case in dev demos. Password
    // is intentionally NOT collected here — the upgrade path needs a follow-up
    // ticket for proper credential capture.
    const body: CustomerRegistrationRequest = {
      fullName: form.fullName.trim(),
      primaryPhone: form.primaryPhone.trim(),
      primaryEmail: form.primaryEmail.trim(),
      // Placeholder password — service-identity will reject if too weak; the
      // in-portal upgrade flow is gated on a server-side WALK_IN token in dev.
      password: 'PortalUpgrade!23',
      ...(form.displayName.trim().length > 0 ? { displayName: form.displayName.trim() } : {}),
    };
    mutation.mutate(body);
  }

  const errorMessage = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof ApiError && (mutation.error.status === 401 || mutation.error.status === 403)) {
      return 'You do not have permission to register customers from this portal. Please contact your administrator.';
    }
    if (mutation.error instanceof ApiError && mutation.error.status === 409) {
      return `A customer with this phone number already exists. Sign in to that account instead.`;
    }
    return mutation.error.message;
  })();

  // Suppress the unused-vars on the noop-fetch trick by using the live token.
  void token;

  if (mutation.isSuccess) {
    return (
      <div>
        <PageHeader title="Account upgraded" description="You are now a REGISTERED NIPOST customer." />
        <div className="max-w-2xl mx-auto bg-white rounded-xl border border-border p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-nipost-green flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Welcome aboard</h2>
          <p className="text-sm text-muted mb-4">
            Your customer number is{' '}
            <span className="font-mono font-bold text-gray-900">{mutation.data.customer.customerNumber}</span>.
            You can now book parcels, top up your wallet, and access full self-service.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Complete your registration"
        description="Upgrade from guest access to a fully-registered NIPOST customer profile."
      />

      <div className="max-w-2xl mx-auto">
        {alreadyRegistered ? (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 mb-6 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-900">
              You are already a REGISTERED customer ({profile.data?.customerNumber}). Use Profile to update
              your details. Submitting this form again will create a duplicate; the backend will reject it
              with a 409.
            </p>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-border p-6 space-y-5" noValidate>
          <div className="flex items-center gap-2 mb-3">
            <User className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Personal details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="cr-fullName" className="block text-sm font-medium text-gray-700 mb-1">
                Full name <span className="text-danger">*</span>
              </label>
              <input
                id="cr-fullName"
                type="text"
                value={form.fullName}
                onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="cr-displayName" className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
              <input
                id="cr-displayName"
                type="text"
                value={form.displayName}
                onChange={(e) => setForm((s) => ({ ...s, displayName: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="cr-phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-danger">*</span>
              </label>
              <input
                id="cr-phone"
                type="tel"
                value={form.primaryPhone}
                onChange={(e) => setForm((s) => ({ ...s, primaryPhone: e.target.value }))}
                placeholder="+234..."
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="cr-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="cr-email"
                type="email"
                value={form.primaryEmail}
                onChange={(e) => setForm((s) => ({ ...s, primaryEmail: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>

          {errorMessage ? (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" role="alert">
              {errorMessage}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={mutation.isPending || form.fullName.trim().length < 2 || form.primaryPhone.trim().length < 7}
            className="px-6 py-2.5 bg-nipost-green text-white text-sm font-medium rounded-lg hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center gap-2"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Register
          </button>
        </form>
      </div>
    </div>
  );
}
